import { Alert, Badge, Button, Card, EmptyState, Spinner } from '@cartly/ui';
import { ShieldCheck } from 'lucide-react';
import { api } from '../../api/client';
import { formatDate, formatRelative } from '../../app/format';
import { useLoader } from '../../app/hooks';
import { useActiveSession } from '../../app/session';

interface PrivacyRequestRow {
  id: string;
  type: 'customer_export' | 'customer_erasure' | 'shop_erasure';
  source: string;
  status: 'received' | 'processing' | 'completed' | 'failed';
  shopDomain: string | null;
  summary: string | null;
  error: string | null;
  hasExport: boolean;
  dueAt: string;
  receivedAt: string;
  completedAt: string | null;
}

const TYPE_LABELS: Record<PrivacyRequestRow['type'], string> = {
  customer_export: 'Customer data request',
  customer_erasure: 'Customer erasure',
  shop_erasure: 'Store data erasure',
};

export function PrivacySettings() {
  const session = useActiveSession();
  const { data, error, loading } = useLoader(
    () => api.get<{ items: PrivacyRequestRow[] }>('/privacy-requests').then((r) => r.items),
    session.activeTenant?.id,
  );

  if (loading && !data) return <Spinner size="lg" label="Loading" />;
  if (error || !data) return <Alert tone="critical">{error ?? 'Failed to load'}</Alert>;

  return (
    <Card
      title="Privacy requests"
      subtitle="Customer data requests and erasures received from Shopify. Erasures run automatically; data requests produce a file you give to the customer within 30 days."
      flush
    >
      {data.length === 0 ? (
        <EmptyState icon={<ShieldCheck size={22} />} title="No privacy requests">
          Requests from Shopify (GDPR / CCPA) appear here with their deadline and outcome.
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Request</th>
                <th>Status</th>
                <th>Received</th>
                <th>Due</th>
                <th className="table__actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="table__primary">{TYPE_LABELS[r.type]}</div>
                    <div className="table__secondary">{r.shopDomain ?? r.source}</div>
                    {r.summary && <div className="table__secondary">{r.summary}</div>}
                    {r.error && <div className="field__error">{r.error}</div>}
                  </td>
                  <td>
                    <Badge
                      tone={
                        r.status === 'completed'
                          ? 'success'
                          : r.status === 'failed'
                            ? 'critical'
                            : 'info'
                      }
                    >
                      {r.status}
                    </Badge>
                  </td>
                  <td className="text-muted text-sm">{formatRelative(r.receivedAt)}</td>
                  <td className="text-sm">{formatDate(r.dueAt)}</td>
                  <td className="table__actions">
                    {r.hasExport && (
                      <a
                        className="btn btn--secondary btn--sm"
                        href={`/api/v1/privacy-requests/${r.id}/export`}
                      >
                        Download data
                      </a>
                    )}
                    {r.status === 'failed' && (
                      <Button size="sm" variant="ghost" disabled>
                        Retrying
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
